import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

import { useOrgCharts } from '../../../hooks';
import {
  big_tech_probe_path,
  create_company_index_path,
  crypto_powerlist_path,
  edit_profile_settings_path,
  enterprise_powerlist_path,
  events_path,
  forum_path,
  gigafactory_database_path,
  highlight_path,
  intel_survey_path,
  kids_tech_survey_path,
  newsletter_list_path,
  opinion_feed_path,
  org_charts_path,
  partners_list_path,
  root_path,
  static_pressed_path,
  subscriber_directories_path,
  tech_ipo_tracker_path,
  ti_50_list_path,
  vc_diversity_index_path,
  video_library_path,
  weekend_path,
} from '../../../routes';
import { NavCurrentUser } from '../../../utils/backend-api/currentUser';
import { Tooltip } from '../../BaseUI';
import NavDropdown from '../NavDropdown';
import sideNavStyles from '../SideNav/styles.module.scss';
import styles from './styles.module.scss';

const cookies = new Cookies();

const PROJECTS = [
  { title: 'Gigafactory Database', url: gigafactory_database_path() },
  { title: 'The Information 50', url: ti_50_list_path() },
  { title: 'Tech IPO Tracker', url: tech_ipo_tracker_path() },
  { title: 'Small Business Survey Results', url: intel_survey_path() },
  { title: 'Enterprise Tech Power List', url: enterprise_powerlist_path() },
  { title: 'Kids and Technology Survey', url: kids_tech_survey_path() },
  { title: 'Crypto Power List', url: crypto_powerlist_path() },
  { title: 'Creator Economy Database', url: create_company_index_path() },
  { title: 'VC Diversity Index', url: vc_diversity_index_path() },
  { title: 'Big Tech Probe Tracker', url: big_tech_probe_path() },
  { title: 'Pressed Documentary', url: static_pressed_path() },
];

const EVENTS = [
  { title: 'Upcoming Events', url: events_path() },
  { title: 'Video Library', url: video_library_path() },
];

type variant = 'top' | 'side';
type tooltipShown = 'directory' | 'forum' | null;

const TOOLTIP_LOAD_DELAY = 2000;

export default function NavLinks({
  currentUser,
  dropdownShown,
  isWebview,
  setDropdownShown,
  variant,
  newForumPostsCount,
}: {
  currentUser: NavCurrentUser;
  dropdownShown: string;
  isWebview?: boolean;
  setDropdownShown: React.Dispatch<React.SetStateAction<string>>;
  variant: variant;
  newForumPostsCount: number;
}) {
  const { orgCharts } = useOrgCharts();
  const [tooltipShown, setTooltipShown] = useState<tooltipShown>(null);

  const inSideNav = variant === 'side';
  const inTopNav = variant === 'top';

  // For Directory/Forum Launch - TODO: remove after launch
  useEffect(() => {
    if (inTopNav) {
      const hasVisitedHomePage = cookies.get('hasVisitedHomePage');
      cookies.set(
        'hasVisitedHomePage',
        (hasVisitedHomePage ? parseInt(hasVisitedHomePage) || 0 : 0) + 1
      );
    }
  }, [inTopNav]);

  useEffect(() => {
    setTimeout(() => setTooltipShown('directory'), TOOLTIP_LOAD_DELAY);
  }, []);

  const allowTooltip =
    (!cookies.get('hasVisitedHomePage') || cookies.get('hasVisitedHomePage') == 1) &&
    window.location.pathname === root_path() &&
    inTopNav &&
    !isWebview &&
    currentUser.isSubscriber;

  return (
    <>
      <div
        className={clsx(styles.firstSection, {
          [styles.firstSideNavSection]: inSideNav,
          [sideNavStyles.sideNavSection]: inSideNav,
        })}
      >
        <a className={styles.navLink} href={newsletter_list_path()}>
          Newsletters
        </a>
        <a className={styles.navLink} href={weekend_path()}>
          Weekend
        </a>
        <a className={styles.navLink} href={opinion_feed_path()}>
          Opinion
        </a>
        {inTopNav ? (
          <a className={styles.navLink} href={org_charts_path()}>
            Org Charts
          </a>
        ) : (
          <NavDropdown
            title="Org Charts"
            options={orgCharts}
            fullWidth
            inSideNav={inSideNav}
            dropdownShown={dropdownShown}
            setDropdownShown={setDropdownShown}
          />
        )}
        <NavDropdown
          title="Projects"
          options={PROJECTS}
          inSideNav={inSideNav}
          dropdownShown={dropdownShown}
          setDropdownShown={setDropdownShown}
        />
        <a className={styles.navLink} href={highlight_path('the-electric')}>
          The Electric
        </a>
        <NavDropdown
          title="Events"
          options={EVENTS}
          inSideNav={inSideNav}
          dropdownShown={dropdownShown}
          setDropdownShown={setDropdownShown}
        />
        <a className={styles.navLink} href={partners_list_path()}>
          Partners
        </a>
      </div>
      <div
        className={clsx({
          [styles.secondTopNavSection]: inTopNav,
          [styles.secondSideNavSection]: inSideNav,
          [sideNavStyles.sideNavSection]: inSideNav,
        })}
      >
        <div className={clsx(styles.linkAndTooltipWrapper)}>
          {/* If we want nav link with new wrapper, use this wrapper */}
          {/* <div className={clsx(styles.newLinkWrapper)}>
            <div className={clsx(styles.newLinkWrapperText)}>NEW!</div>
            <a className={clsx(styles.navLink, styles.newLink)}>
              SOME LINK TEXT
            </a>
          </div> */}
          <div className={clsx(styles.navLink)}>
            <a href={subscriber_directories_path()}>Directory</a>
          </div>
          {/* // For Directory/Forum Launch - TODO: remove after launch */}
          {allowTooltip && (
            <Tooltip
              onClose={() => setTooltipShown('forum')}
              link={<a href={edit_profile_settings_path()}>Edit my profile</a>}
              message="Set up your profile to join our Subscriber Directory"
              showTooltip={tooltipShown === 'directory'}
              tooltipId="directory-tooltip"
            />
          )}
        </div>
        <div className={clsx(styles.linkAndTooltipWrapper)}>
          <div className={clsx(styles.navLink, styles.noMargin)}>
            <a href={forum_path()} data-test="nav-link-forum">
              Forum{newForumPostsCount > 0 && ` (${newForumPostsCount})`}
            </a>
          </div>
          {allowTooltip && (
            <Tooltip
              onClose={() => setTooltipShown(null)}
              link={<a href={forum_path()}>Check it out!</a>}
              message="Join our forum to start a new discussion"
              showTooltip={tooltipShown === 'forum'}
              tooltipId="forum-tooltip"
            />
          )}
        </div>
      </div>
    </>
  );
}
